import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { FlagOutlined, RightOutlined } from "@ant-design/icons";
import customClientAxios from "../customAxios";
import * as Api from "../utils/ApiEndpoints";
import { useAuth } from "../AuthContext";

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Bar = styled.button`
  width: 100%;
  display: flex; align-items: center; gap: 16px;
  padding: 10px 22px;
  background: linear-gradient(90deg, #4f46e5 0%, #7c3aed 60%, #a855f7 100%);
  color: #fff;
  border: none;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  animation: ${slideDown} 0.25s ease;
  position: relative;
  overflow: hidden;

  &:hover .chev { transform: translateX(3px); }
`;

const IconBox = styled.div`
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; border-radius: 8px;
  background: rgba(255,255,255,0.16);
  flex-shrink: 0;
`;

const MainText = styled.div`
  flex: 1;
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
  min-width: 0;
`;

const Title = styled.div`
  font-size: 13px; font-weight: 600; opacity: 0.92;
  letter-spacing: 0.2px;
  white-space: nowrap;
  overflow: hidden; text-overflow: ellipsis;
  max-width: 38ch;
`;

const Pill = styled.div`
  display: inline-flex; align-items: center; gap: 6px;
  background: rgba(255,255,255,0.15);
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
`;

const Progress = styled.div`
  flex: 1;
  height: 8px; background: rgba(255,255,255,0.18); border-radius: 999px;
  overflow: hidden; min-width: 120px; max-width: 280px;
`;
const ProgressFill = styled.div`
  height: 100%; background: rgba(255,255,255,0.95);
  width: ${(p) => p.$pct}%;
  transition: width 0.4s ease;
`;

const Chev = styled.div`
  display: flex; align-items: center; gap: 6px;
  font-size: 13px; opacity: 0.9; white-space: nowrap;
  transition: transform 0.2s ease;
`;

const formatCompactINR = (paise) => {
  const rupees = (paise || 0) / 100;
  if (rupees >= 10000000) return `₹${(rupees / 10000000).toFixed(2)} Cr`;
  if (rupees >= 100000) return `₹${(rupees / 100000).toFixed(2)} L`;
  if (rupees >= 1000) return `₹${(rupees / 1000).toFixed(1)}k`;
  return `₹${Math.round(rupees)}`;
};

const POLL_MS = 60_000;

export default function GoalsBanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn } = useAuth();
  const [state, setState] = useState({ loaded: false, payload: null });

  const fetchOnce = useCallback(async () => {
    try {
      const r = await customClientAxios().get(Api.AdminGoalActiveProgress);
      setState({ loaded: true, payload: r.data?.data || null });
    } catch {
      setState({ loaded: true, payload: null });
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return undefined;
    fetchOnce();
    const id = setInterval(fetchOnce, POLL_MS);
    return () => clearInterval(id);
  }, [fetchOnce, isLoggedIn]);

  // Don't show on login page or on the Goals page itself.
  if (!isLoggedIn) return null;
  if (location.pathname.startsWith("/goals")) return null;
  if (!state.loaded) return null;
  if (!state.payload || !state.payload.goal) return null;

  const { goal, progress } = state.payload;
  if (!goal || !progress) return null;

  const pct = Math.min(100, progress.percent || 0);

  return (
    <Bar onClick={() => navigate("/goals")} title="Open Goals">
      <IconBox><FlagOutlined style={{ fontSize: 16 }} /></IconBox>
      <MainText>
        <Title>🎯 {goal.title}</Title>
        <Pill>
          {formatCompactINR(progress.totalRevenue)} / {formatCompactINR(progress.targetAmount)}
        </Pill>
        <Progress><ProgressFill $pct={pct} /></Progress>
        <Pill>{pct.toFixed(1)}%</Pill>
        <Pill>{progress.payingUsersCount} paying</Pill>
        <Pill>
          {progress.isOverdue
            ? `${Math.abs(progress.daysLeft)}d overdue`
            : `${progress.daysLeft}d left`}
        </Pill>
      </MainText>
      <Chev className="chev">
        View <RightOutlined />
      </Chev>
    </Bar>
  );
}
